// viewState.ts — the repo page's whole view state, encoded in the URL.
//
// `?exp=<slug>&sel=<sha>,<sha>&descendants=0&order=lineage` is the
// shareable form of "this experiment, these commits, without its descendant
// experiments' branches, listed this way": paste the URL and the other
// person sees the same three panes in the same order.
//
// Closed (consolidated or abandoned) experiments and their branches are
// hidden by default; `closed=1` shows them (closed.ts).
//
// Descendants are on by default: an experiment with children is a container
// for them, and a graph showing only the parent's own branch hides the work.
// `descendants=0` is the explicit opt-out; an experiment without children
// draws the same graph either way.
// Nothing about the selection lives in component state, so the browser's
// back button steps through selections and a reload keeps them.

// The /overview route accepts between 1 and 20 shas, so the UI stops the
// selection growing past the server's cap rather than earning a 400.
export const MAX_SELECTION = 20;

// How the experiments list is ordered, server-side: 'recency' puts the
// experiment touched most recently first, 'lineage' walks roots then
// descendants depth-first. Recency is the default because the pane's first
// question is "what have I been working on", and a long-lived repo buries
// that under the oldest root otherwise.
export type ExperimentOrder = 'recency' | 'lineage';

export const DEFAULT_ORDER: ExperimentOrder = 'recency';

export interface RepoViewState {
  /** Selected experiment slug; '' = none resolved yet (repo-wide). */
  experiment: string;
  /** Selected commit shas, in the order they were picked. */
  selection: string[];
  /** Widen the graph to descendant experiments' branches (default on). */
  descendants: boolean;
  /** Server-side ordering of the experiments list. */
  order: ExperimentOrder;
  /** Show consolidated and abandoned experiments and their branches. */
  closed: boolean;
}

// An unrecognised ?order= is the default rather than an error: the value
// comes from a URL someone pasted, and a typo should not blank the pane.
export function readOrder(raw: string | null): ExperimentOrder {
  return raw === 'lineage' ? 'lineage' : DEFAULT_ORDER;
}

export function readViewState(search: URLSearchParams): RepoViewState {
  return {
    experiment: search.get('exp') ?? '',
    selection: (search.get('sel') ?? '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean),
    descendants: search.get('descendants') !== '0',
    order: readOrder(search.get('order')),
    closed: search.get('closed') === '1',
  };
}

// Merge a patch into the existing params, dropping keys set to null or ''
// so a default-valued view produces a clean URL.
export function writeViewState(
  search: URLSearchParams,
  patch: Record<string, string | null>,
): URLSearchParams {
  const next = new URLSearchParams(search);
  for (const [k, v] of Object.entries(patch)) {
    if (v === null || v === '') next.delete(k);
    else next.set(k, v);
  }
  return next;
}

// The URL value for the descendants toggle: on is the default, so it leaves
// the URL clean; off is spelled out.
export function descendantsParam(on: boolean): string | null {
  return on ? null : '0';
}

export function selectionParam(selection: string[]): string | null {
  return selection.length > 0 ? selection.join(',') : null;
}

// Click semantics for a graph row. Plain click replaces the selection
// (clicking the one selected commit again clears it, which is how the pane
// gets emptied without a separate control); ctrl/cmd/shift click adds or
// removes, capped at MAX_SELECTION.
export function nextSelection(
  current: string[],
  sha: string,
  additive: boolean,
): string[] {
  if (!additive) {
    if (current.length === 1 && current[0] === sha) return [];
    return [sha];
  }
  if (current.includes(sha)) return current.filter((s) => s !== sha);
  if (current.length >= MAX_SELECTION) return current;
  return [...current, sha];
}

// True when the mouse event asks for an additive selection.
export function isAdditiveClick(e: {
  ctrlKey?: boolean;
  metaKey?: boolean;
  shiftKey?: boolean;
}): boolean {
  return Boolean(e.ctrlKey || e.metaKey || e.shiftKey);
}
